import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Box, Typography } from '@mui/material';

import useReactHookFormStyles from '../../../../custom-components/form-components/ReactHookForm.styles';
import { generateTestId } from '../../../../../utils';

const Select = ({ name, labelKey, formatLabel, loading, addNullOption, props, key }) => {
    const {
        label,
        rules,
        options,
        variant,
        disabled,
        defaultValue,
        required,
        optionLabel,
        shouldUnregister, // Field should unregister if no longer rendered,
        subtitle, // small text displayed below the input
        highlight,
    } = props;

    const {
        control,
        formState: { errors },
        getValues,
        updateSelectedValues,
    } = useFormContext();

    const classes = useReactHookFormStyles();

    useEffect(() => {
        const value = getValues(name);
        // Update context with full object value when default value exists and/or options change
        if (value) {
            updateSelectedValues(
                name,
                options?.find((opt) => opt._id === value)
            );
        }
    }, [options]);

    const parseLabel = (option, optionList) => {
        const foundOption = typeof option === 'object' ? option : optionList?.find((opt) => (opt.id || opt._id) === option);
        const values = optionLabel.keys.map((val) => {
            const splitKey = val.split('.');
            return splitKey.reduce((acc, curr) => acc?.[curr], foundOption);
        });
        return values.filter(Boolean).join(optionLabel.connector);
    };

    const getOptionLabel = (option) => {
        if (!option && !addNullOption) {
            return '';
        }
        if (formatLabel) {
            return formatLabel(option, options);
        }
        if (optionLabel) {
            return parseLabel(option, options);
        }
        const optionKey = labelKey || 'name';
        if (option[optionKey]) {
            return option[optionKey];
        }
        const foundOption = options?.find((opt) => (opt.id || opt._id) === option);
        return foundOption?.[optionKey] || '';
    };

    const renderInput = (renderProps) => (
        <TextField
            {...renderProps}
            label={label}
            required={required}
            variant={variant || 'outlined'}
            error={errors[name]}
            helperText={errors[name]?.message}
            data-testid={generateTestId(name, 'select-autocomplete-input')}
            // wrap
        />
    );

    const getRenderOption = (option) => {
        const highlightId = getValues(highlight?.field);
        const optionId = option?._id || option?.id || option;
        const isHighlighted = highlightId && optionId === highlightId;

        return (
            <>
                {getOptionLabel(option)}
                {isHighlighted && highlight?.icon && (
                    <Box
                        component="span"
                        sx={{
                            ml: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                        }}
                    >
                        {highlight.icon}
                    </Box>
                )}
            </>
        );
    };

    if (loading) {
        return (
            <TextField
                value="Loading options"
                disabled
                label={label}
                variant={variant || 'outlined'}
                className={classes.textField}
                // wrap
            />
        );
    }

    const handleChange = (item, onChange) => {
        const val = item?.id || item?._id;
        if (val === null || val === false) {
            onChange(val);
            return;
        }
        if (addNullOption && item === null) {
            onChange(undefined);
            return;
        }
        onChange(val ?? item);
    };

    return (
        <FormControl className={classes.formController}>
            <Controller
                name={name}
                control={control}
                rules={rules}
                defaultValue={defaultValue}
                shouldUnregister={shouldUnregister}
                render={({ field: { onChange, value } }) => {
                    const fallbackValue = () => {
                        const currentValue = value || value === null ? value : defaultValue;
                        if (currentValue) {
                            if (typeof actualValue !== 'object') {
                                return options?.find((opt) => (opt.id || opt._id) === currentValue) ?? currentValue;
                            }
                            return currentValue;
                        }
                        if (props.multiple) {
                            return [];
                        }
                        if (addNullOption && currentValue !== undefined) {
                            return addNullOption;
                        }
                        return null;
                    };
                    return (
                        <Autocomplete
                            {...props}
                            value={fallbackValue()}
                            onChange={(e, item) => {
                                updateSelectedValues(name, item);
                                // When multiple=true, handle array of values
                                if (Array.isArray(item)) {
                                    const mappedItem = item.map((i) => i.id ?? i._id ?? i);
                                    onChange(mappedItem);
                                } else {
                                    handleChange(item, onChange);
                                }
                            }}
                            options={options}
                            disabled={disabled}
                            renderInput={renderInput}
                            getOptionLabel={getOptionLabel}
                            renderOption={getRenderOption}
                            getOptionSelected={(option, val) => {
                                const valId = val?.id || val?._id || val;
                                const optionId = option?.id || option?._id || option;
                                return valId === optionId;
                            }}
                            key={key}
                            data-testid={generateTestId(name, 'select-autocomplete')}
                            // wrap
                        />
                    );
                }}
            />
            {subtitle && (
                <Box padding={0.5}>
                    <Typography variant="body2" color="textSecondary" data-testid={generateTestId(name, 'select-autocomplete-subtitle')}>
                        {subtitle}
                    </Typography>
                </Box>
            )}
        </FormControl>
    );
};

export default Select;
